import MoreVertIcon from '@mui/icons-material/MoreVert'
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'
import { useState } from 'react'

import { staffEnabledActionLabel } from '../../utils/staffEnabledCopy'

export interface StaffActionsMenuProps {
  /** Names the staff member in the trigger's label, which is otherwise identical on every card. */
  staffName: string
  /** Decides both what the action is called and which way it reads. */
  isStaffEnabled: boolean
  /** This staff member's own change is in flight, which is what the spinner is for. */
  isActionPending: boolean
  /** No change can be started right now, whoever it would be for. */
  isActionDisabled: boolean
  onSelectAction: () => void
}

const PROGRESS_SIZE = 20

/**
 * The actions available on one staff member.
 *
 * A menu rather than a button on the card, because the action it holds takes a
 * person's access away and should not be a stray click on a list being skimmed.
 *
 * Closes before the action runs, so the confirmation is not raised underneath it.
 */
export const StaffActionsMenu = ({
  staffName,
  isStaffEnabled,
  isActionPending,
  isActionDisabled,
  onSelectAction,
}: StaffActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const handleSelect = () => {
    setAnchorEl(null)
    onSelectAction()
  }

  return (
    <>
      <IconButton
        size='small'
        aria-label={`Actions for ${staffName}`}
        aria-haspopup='true'
        aria-expanded={isOpen}
        disabled={isActionPending || isActionDisabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {isActionPending ? (
          <CircularProgress size={PROGRESS_SIZE} />
        ) : (
          <MoreVertIcon fontSize='small' />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { variant: 'actionMenu' } }}
        // Dropped below the trigger and aligned to its right edge, so the menu
        // does not cover the staff member it is about.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          variant={isStaffEnabled ? 'destructiveAction' : 'restorativeAction'}
          onClick={handleSelect}
        >
          {staffEnabledActionLabel(isStaffEnabled)}
        </MenuItem>
      </Menu>
    </>
  )
}
